// Quầy trừ buổi: tìm khách theo SĐT / tên / mã thẻ → chọn thẻ → trừ buổi; check-in lịch hẹn hôm nay
import { useEffect, useMemo, useRef, useState } from 'react';
import { Empty, Segmented, message } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { CalendarPlus, CircleDollarSign, MessagesSquare, Search, UserRound } from 'lucide-react';
import { branchName, staffName } from '../POS/data';
import usePosStore from '../POS/store';
import { formatPhone } from '../POS/utils';
import type { CardBooking, TreatmentCard } from './data';
import { cardFlags, expiryText } from './logic';
import useTreatmentStore from './store';
import { useTreatmentAuth } from './actions';
import { BookingModal, DeductModal } from './modals';
import PayRequestModal, { pendingRequestOf } from './PayRequestModal';
import CardDetail from './CardDetail';
import TreatmentShell, { BalanceCell, CARD_STATUS, FlagPills, Pill, money } from './ui';
import { CheckInVisitBar } from '../Customer360/VisitWidgets';
import { useTuVan } from '../AppTuVan/store';
import { cardReferral, consultVisitOf, needsConsult, referToConsult } from './consult';

const normalize = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase();
const STATUS_ORDER = { active: 0, expired: 1, completed: 2, refunded: 3, cancelled: 4 } as const;

export default function TreatmentCheckIn() {
  const cards = useTreatmentStore(s => s.cards);
  const cfg = useTreatmentStore(s => s.config);
  const requests = useTreatmentStore(s => s.payRequests);
  const workingBranch = usePosStore(s => s.workingBranch);
  const { can, userName } = useTreatmentAuth();
  const navigate = useNavigate();
  const tuVisits = useTuVan(s => s.visits);
  // Mở từ Lễ tân sau khi khách làm xong dịch vụ: SĐT truyền qua state (không đưa lên URL) điền sẵn ô tìm
  const loc = useLocation();
  const [q, setQ] = useState(((loc.state ?? {}) as { q?: string }).q ?? '');
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [scope, setScope] = useState<'branch' | 'all'>('branch');
  const [deduct, setDeduct] = useState<{ card: TreatmentCard; booking: CardBooking | null } | null>(null);
  const [bookingCard, setBookingCard] = useState<TreatmentCard | null>(null);
  const [payCardState, setPayCard] = useState<TreatmentCard | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  // Khách có thẻ, gom theo khách
  const customers = useMemo(() => {
    const map = new Map<string, { id: string; name: string; phone: string; cards: TreatmentCard[] }>();
    cards.forEach(c => {
      const cur = map.get(c.customerId) || { id: c.customerId, name: c.customerName, phone: c.customerPhone, cards: [] };
      cur.cards.push(c);
      map.set(c.customerId, cur);
    });
    return Array.from(map.values());
  }, [cards]);

  const results = useMemo(() => {
    const t = q.trim();
    if (t.length < 2) return [];
    const nq = normalize(t);
    const digits = t.replace(/\D/g, '');
    return customers.filter(c =>
      (digits.length >= 3 && c.phone.includes(digits)) || normalize(c.name).includes(nq) || c.cards.some(x => x.id.toLowerCase() === nq || x.id.toLowerCase().endsWith(nq))).slice(0, 8);
  }, [customers, q]);

  const selected = customers.find(c => c.id === customerId);
  const selectedCards = selected ? [...selected.cards].sort((a, b) => STATUS_ORDER[cardFlags(a, cfg).status] - STATUS_ORDER[cardFlags(b, cfg).status] || b.createdAt.localeCompare(a.createdAt)) : [];

  const today = dayjs().format('YYYY-MM-DD');
  const todayBookings = useMemo(() => cards.flatMap(c => c.bookings
    .filter(b => dayjs(b.at).format('YYYY-MM-DD') === today && b.status !== 'cancelled' && (scope === 'all' || b.branchId === workingBranch))
    .map(b => ({ b, c }))).sort((x, y) => x.b.at.localeCompare(y.b.at)), [cards, today, scope, workingBranch]);
  const todayUsages = useMemo(() => cards.flatMap(c => c.usages
    .filter(u => !u.cancelled && dayjs(u.at).format('YYYY-MM-DD') === today && (scope === 'all' || u.branchId === workingBranch))
    .map(u => ({ u, c }))).sort((x, y) => y.u.at.localeCompare(x.u.at)), [cards, today, scope, workingBranch]);

  const pick = (id: string) => { setCustomerId(id); setQ(''); };

  // Chuyển khách sang Tư vấn cơ sở: thẻ hết / sắp hết thì gợi ý (nút màu cam)
  const inConsult = selected ? consultVisitOf(tuVisits, selected.phone) : null;
  const suggest = !!selected && needsConsult(selected.cards, cfg);
  const toConsult = () => {
    if (!selected) return;
    const { created } = referToConsult(cardReferral(selected, selected.cards, cfg), userName);
    message.success(created ? `Đã chuyển ${selected.name} sang Tư vấn cơ sở — tư vấn viên rảnh sẽ được chia tự động.` : `${selected.name} đã có trong hàng chờ tư vấn.`);
  };

  return (
    <TreatmentShell title="Trừ buổi liệu trình" subtitle="Tìm khách bằng SĐT, tên hoặc mã thẻ; mỗi lần khách đến làm, trừ buổi và cho khách ký xác nhận.">
      <div className="grid gap-4 lg:grid-cols-[1fr_380px]">
        <section className="space-y-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
            <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && results.length === 1) pick(results[0].id); }}
              placeholder="SĐT, tên khách hoặc mã thẻ (TLT-…)"
              className="h-12 w-full rounded-xl border border-slate-300 bg-white pl-11 pr-3 text-base outline-none focus:border-[#3e91ab] focus:ring-2 focus:ring-[#3e91ab]/20" />
            {q.trim().length >= 2 && (
              <div className="absolute left-0 right-0 top-full z-20 mt-1 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
                {results.length === 0 && <div className="px-4 py-3 text-sm text-slate-500">Không tìm thấy khách có thẻ liệu trình</div>}
                {results.map(c => {
                  const activeCount = c.cards.filter(x => cardFlags(x, cfg).status === 'active').length;
                  return (
                    <button key={c.id} onClick={() => pick(c.id)} className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-[#ecf6f9]">
                      <span><b>{c.name}</b> <span className="text-sm text-slate-500">{formatPhone(c.phone)}</span></span>
                      <span className="text-xs text-slate-500">{activeCount} thẻ đang dùng / {c.cards.length}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {!selected ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500">
              <UserRound className="mx-auto mb-2 h-10 w-10 text-slate-300" />
              Tìm khách để xem các thẻ liệu trình, hoặc bấm <b>Check-in</b> ở lịch hẹn hôm nay.
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div><span className="text-lg font-bold">{selected.name}</span> <span className="text-slate-500">{formatPhone(selected.phone)}</span></div>
                <div className="flex items-center gap-3">
                  {inConsult
                    ? <button onClick={() => navigate('/app-tuvan')} className="flex h-9 items-center gap-1.5 rounded-lg border border-green-300 bg-green-50 px-3 text-sm font-semibold text-green-700"><MessagesSquare className="h-4 w-4" />{inConsult.status === 'consulting' ? 'Đang tư vấn' : 'Đang chờ tư vấn'}</button>
                    : <button onClick={toConsult} title="Đưa khách vào hàng chờ Tư vấn cơ sở — tư vấn viên rảnh được chia tự động"
                      className={`flex h-9 items-center gap-1.5 rounded-lg px-3 text-sm font-semibold ${suggest ? 'bg-amber-500 text-white hover:bg-amber-600' : 'border border-slate-300 hover:border-[#3e91ab]'}`}>
                      <MessagesSquare className="h-4 w-4" />Chuyển tư vấn</button>}
                  <button onClick={() => setCustomerId(null)} className="text-sm text-slate-500 hover:text-[#2c6b7f]">Đổi khách</button>
                </div>
              </div>
              {!inConsult && suggest && <div className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">Khách có thẻ đã hết / sắp hết — nên chuyển Tư vấn cơ sở để tư vấn gói tiếp theo.</div>}
              {/* Khách làm xong mọi dịch vụ → ký một lần cho cả lần đến */}
              <CheckInVisitBar posCustomerId={selected.id} name={selected.name} phone={selected.phone} />
              {selectedCards.map(c => {
                const f = cardFlags(c, cfg);
                const usable = f.status === 'active';
                return (
                  <div key={c.id} className={`rounded-xl border bg-white p-4 shadow-sm ${usable ? 'border-slate-200' : 'border-slate-100 opacity-70'}`}>
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <button onClick={() => setDetailId(c.id)} className="text-left font-bold text-slate-800 hover:text-[#2c6b7f]">{c.packageName}</button>
                        <div className="text-xs text-slate-500"><span className="font-mono">{c.id}</span> · hạn {expiryText(c)}</div>
                      </div>
                      <span className="flex flex-wrap items-center gap-1"><Pill {...CARD_STATUS[f.status]} /><FlagPills f={f} compact /></span>
                    </div>
                    <div className="mt-3 flex flex-wrap items-end justify-between gap-3">
                      <BalanceCell card={c} />
                      {f.debt > 0 && <div className="text-sm">Còn nợ <b className={f.overdue ? 'text-red-600' : 'text-amber-700'}>{money(f.debt)}đ</b></div>}
                      {can('create') && (
                        <div className="flex flex-wrap gap-2">
                          {f.debt > 0 && (pendingRequestOf(requests, c.id)
                            ? <button onClick={() => setPayCard(c)} className="flex h-10 items-center gap-1 rounded-lg border border-amber-300 bg-amber-50 px-3 text-sm font-semibold text-amber-800"><CircleDollarSign className="h-4 w-4" /> Chờ POS thu…</button>
                            : <button onClick={() => setPayCard(c)} className="flex h-10 items-center gap-1 rounded-lg border border-slate-300 px-3 text-sm font-semibold hover:border-[#3e91ab]"><CircleDollarSign className="h-4 w-4" /> Gửi POS thu</button>)}
                          <button disabled={!usable} onClick={() => setBookingCard(c)} className="flex h-10 items-center gap-1 rounded-lg border border-slate-300 px-3 text-sm font-semibold hover:border-[#3e91ab] disabled:opacity-40"><CalendarPlus className="h-4 w-4" /> Đặt lịch</button>
                          <button disabled={!usable} onClick={() => setDeduct({ card: c, booking: null })} className="h-10 rounded-lg bg-[#357f96] px-4 text-sm font-semibold text-white hover:bg-[#2c6b7f] disabled:opacity-40">Trừ buổi</button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <aside className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-500">{branchName(workingBranch)}</span>
            <Segmented size="small" value={scope} onChange={v => setScope(v as 'branch' | 'all')} options={[{ value: 'branch', label: 'Chi nhánh' }, { value: 'all', label: 'Tất cả' }]} />
          </div>
          <div className="rounded-xl border border-slate-200 bg-white">
            <div className="border-b border-slate-100 px-4 py-2.5 font-semibold">Lịch hẹn hôm nay ({todayBookings.length})</div>
            {todayBookings.length === 0 ? <Empty className="!my-6" image={Empty.PRESENTED_IMAGE_SIMPLE} description="Không có lịch hẹn" /> : (
              <div className="divide-y divide-slate-100">
                {todayBookings.map(({ b, c }) => (
                  <div key={b.id} className="flex items-center justify-between gap-2 px-4 py-2.5 text-sm">
                    <button onClick={() => setDetailId(c.id)} className="min-w-0 text-left">
                      <div><b className="text-[#2c6b7f]">{dayjs(b.at).format('HH:mm')}</b> · <b>{c.customerName}</b></div>
                      <div className="truncate text-xs text-slate-500">{b.name}{b.staffId && ` · KTV ${staffName(b.staffId)}`}{scope === 'all' && ` · ${branchName(b.branchId)}`}</div>
                    </button>
                    {b.status === 'booked'
                      ? can('create') && <button onClick={() => setDeduct({ card: c, booking: b })} className="shrink-0 rounded-lg bg-[#357f96] px-3 py-1.5 text-xs font-semibold text-white">Check-in</button>
                      : <Pill label={b.status === 'done' ? 'Đã làm' : 'Không đến'} cls={b.status === 'done' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-600 border-red-200'} />}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="rounded-xl border border-slate-200 bg-white">
            <div className="border-b border-slate-100 px-4 py-2.5 font-semibold">Đã trừ hôm nay ({todayUsages.length})</div>
            {todayUsages.length === 0 ? <Empty className="!my-6" image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có lượt nào" /> : (
              <div className="divide-y divide-slate-100">
                {todayUsages.map(({ u, c }) => (
                  <button key={u.id} onClick={() => setDetailId(c.id)} className="block w-full px-4 py-2.5 text-left text-sm hover:bg-slate-50">
                    <div><b>{dayjs(u.at).format('HH:mm')}</b> · {c.customerName}</div>
                    <div className="text-xs text-slate-500">{u.items.map(i => `${i.name} ×${i.qty}`).join(', ')} · KTV {staffName(u.staffId)}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </aside>
      </div>

      <DeductModal card={deduct?.card || null} booking={deduct?.booking} onClose={() => setDeduct(null)} onDone={() => deduct && setCustomerId(deduct.card.customerId)} />
      <BookingModal card={bookingCard} onClose={() => setBookingCard(null)} />
      <PayRequestModal card={payCardState} onClose={() => setPayCard(null)} />
      <CardDetail cardId={detailId} onClose={() => setDetailId(null)} />
    </TreatmentShell>
  );
}
